import { useCallback, useEffect, useMemo } from 'react';

import type { EditingMode, GQL, UUID } from '@aline/common';
import {
  AlineTransforms,
  CANNED_FIELD_METADATA,
  isCannedFieldElement,
  PDFCustomDataKey,
  Queries,
} from '@aline/common';
import type { ApolloLink, Reference } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type { Core } from '@pdftron/webviewer';
import type { Range } from 'slate';
import { Editor } from 'slate';
import { getVariablePanelFocusedField } from 'src/components/variables';
import type { PDFFieldLocation } from 'src/components/viewer/hooks/crud';
import { usePDFFieldAPI } from 'src/components/viewer/hooks/crud';
import { pdfTronCore, useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useAreAnnotationsLoaded } from 'src/components/viewer/viewer-hooks';
import {
  useEditorTabStateSelectorForTabId,
  useGetFieldsInJSON,
} from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification, workflowTypename } from 'src/helpers';
import { useMaybeEditor } from 'src/layout-engine';
import type { AlineClientEditor } from 'src/layout-engine/hooks';
import { v4 as uuid4 } from 'uuid';

import { useDocumentSignatureStatus, useGetCurrentDoc } from './document';

/**
 * Apollo merges the result into the `CannedField` entity on its own, but
 * `Workflow.cannedFields` holds a list of refs and nothing in the response says
 * the row belongs in it. The Outline and both value-sync hooks read that list,
 * so a placement stays invisible until the list itself gets the ref.
 */
export function useCreateCannedField(): {
  createCannedField: (
    options: Parameters<Apollo.CreateCannedFieldMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateCannedFieldMutation>>;
} {
  const [createCannedField] = Apollo.useCreateCannedFieldMutation();

  const safeCreateCannedField = useCallback<
    ReturnType<typeof useCreateCannedField>['createCannedField']
  >(
    options =>
      createCannedField({
        ...options,
        update(cache, { data }) {
          const created = data?.createCannedField.cannedField;
          if (!created) return;

          cache.modify({
            fields: {
              cannedFields: (refs = [], { readField, toReference }) => {
                /**
                 * A create for a field another document already holds returns
                 * that existing row, which is in the list, so appending blindly
                 * would show the same field twice until the next refetch.
                 */
                if (refs.some((ref: Reference) => readField<UUID>('id', ref) === created.id))
                  return refs;

                const ref = toReference(created, true);
                return ref ? [...refs, ref] : refs;
              },
            },
            id: cache.identify({ __typename: workflowTypename, id: created.workflowId }),
          });
        },
      }),
    [createCannedField]
  );

  return { createCannedField: safeCreateCannedField };
}

/**
 * Tombstones the row itself, which is the workflow's whole definition of the
 * field. Callers own the document content; this only takes the row out of the
 * workflow list so the Outline stops offering a field nothing backs.
 */
export function useDeleteCannedField(): {
  deleteCannedField: (
    options: Parameters<Apollo.DeleteCannedFieldMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.DeleteCannedFieldMutation>>;
} {
  const [deleteCannedField] = Apollo.useDeleteCannedFieldMutation();

  const safeDeleteCannedField = useCallback<
    ReturnType<typeof useDeleteCannedField>['deleteCannedField']
  >(
    options =>
      deleteCannedField({
        ...options,
        update(cache, { data }) {
          const deleted = data?.deleteCannedField.cannedField;
          if (!deleted) return;

          cache.modify({
            fields: {
              cannedFields: (refs = [], { readField }) =>
                refs.filter((ref: Reference) => readField<UUID>('id', ref) !== deleted.id),
            },
            id: cache.identify({ __typename: workflowTypename, id: deleted.workflowId }),
          });
        },
      }),
    [deleteCannedField]
  );

  return { deleteCannedField: safeDeleteCannedField };
}

type CannedFieldTarget =
  | {
      __typename: 'Slate';
      editingMode: EditingMode;
      editor: AlineClientEditor;
      at?: Range;
    }
  | {
      __typename: 'Pdf';
      location: PDFFieldLocation;
    };

export function useInsertCannedField(
  documentViewer: Core.DocumentViewer | null,
  tabId: UUID | null
): {
  insertCannedField: (args: {
    assigneeId: UUID | null;
    documentId: UUID;
    nodeId: UUID;
    target: CannedFieldTarget;
    variant: GQL.CannedFieldVariant;
    workflowId: UUID;
  }) => Promise<Apollo.CannedFieldFullFragment | null>;
} {
  const client = useApolloClient();
  const { createCannedField } = useCreateCannedField();
  const { createPDFCannedField, deletePDFField } = usePDFFieldAPI(documentViewer, tabId);
  const dispatchFailed = useDispatchNotification({
    message: 'Failed to place canned field.',
    variant: 'error',
  });

  /**
   * A canned field may be placed as many times as the author wants, in one
   * document or across the workflow. Every placement shares the row's
   * `cannedFieldId`, so they all show one value and collapse to a single row in
   * the Outline panel.
   *
   * The node goes into the content before the mutation runs, under the nodeId
   * the workflow's existing row already has, or a fresh one when this variant
   * and assignee has no row yet. The server rejects a create that names an
   * existing field by any other nodeId, so the id in the content is the id the
   * row keeps and nothing has to be rewritten afterwards.
   *
   * The create still runs when the row exists: it is what takes this
   * document's `CannedFieldPlacement` and promotes the workflow file to
   * signing. Only the row itself is deduped server-side.
   *
   * Each call also mints a `placementNodeId` for the occurrence it adds, in the
   * Slate node or on the PDF widget, so a failed create can take that one
   * placement back out without touching the others.
   */
  const insertCannedField = useCallback<
    ReturnType<typeof useInsertCannedField>['insertCannedField']
  >(
    async ({ assigneeId, documentId, nodeId, target, variant, workflowId }) => {
      const cached = client.readQuery<
        Apollo.GetWorkflowCannedFieldsQuery,
        Apollo.GetWorkflowCannedFieldsQueryVariables
      >({
        query: Apollo.GetWorkflowCannedFieldsDocument,
        variables: { workflowId },
      });
      const cachedField = cached?.workflow?.cannedFields.find(
        field => field.assigneeId === assigneeId && field.variant === variant
      );
      const cannedFieldId = cachedField?.nodeId ?? nodeId;
      const placementNodeId = uuid4();

      try {
        if (target.__typename === 'Slate') {
          AlineTransforms.insertCannedField(target.editor, {
            at: target.at,
            cannedFieldId,
            editingMode: target.editingMode,
            placementNodeId,
            variant,
          });
        } else {
          await createPDFCannedField({
            cannedFieldId,
            location: target.location,
            nodeId: placementNodeId,
          });
        }
      } catch {
        // Nothing landed in the content, and no server call was made to report.
        dispatchFailed();
        return null;
      }

      const { data } = await createCannedField({
        variables: {
          input: { assigneeId, documentId, nodeId: cannedFieldId, variant, workflowId },
        },
      });
      /**
       * The client runs `errorPolicy: 'all'`, so a rejected create lands here
       * rather than as a throw, and the error link has already put the server's
       * message on screen.
       */
      if (data) return data.createCannedField.cannedField;

      /**
       * The row the server kept disagrees with the id now sitting in the
       * content, so take this placement back out — by its own
       * `placementNodeId`, since other placements of the same field, here or
       * in another document, are legitimate. A stale list is what produces
       * that id, so refresh it before the user tries again.
       */
      if (target.__typename === 'Slate')
        AlineTransforms.removeCannedFieldPlacement(target.editor, { placementNodeId });
      else await deletePDFField(placementNodeId);

      await refetchWorkflowCannedFields(client, workflowId);
      return null;
    },
    [client, createCannedField, createPDFCannedField, deletePDFField, dispatchFailed]
  );

  return { insertCannedField };
}

async function refetchWorkflowCannedFields(
  client: ReturnType<typeof useApolloClient>,
  workflowId: UUID
): Promise<void> {
  try {
    await client.query<
      Apollo.GetWorkflowCannedFieldsQuery,
      Apollo.GetWorkflowCannedFieldsQueryVariables
    >({
      fetchPolicy: 'network-only',
      query: Apollo.GetWorkflowCannedFieldsDocument,
      variables: { workflowId },
    });
  } catch {
    // Unreachable server — the query's own next read refreshes the list.
  }
}

export function useRemoveCannedFieldFromDocument(): {
  removeCannedFieldFromDocument: (args: {
    /**
     * The row's PK addresses the mutation and the cache row; nodeId addresses
     * Slate/PDF nodes that reference it.
     */
    cannedField: { id: UUID; nodeId: UUID };
    documentId: UUID;
    documentViewer: Core.DocumentViewer | null;
    editor: AlineClientEditor | null;
    tabId: UUID | null;
    workflowId: UUID;
  }) => Promise<void>;
} {
  const client = useApolloClient();
  const { deleteCannedField } = useDeleteCannedField();
  const dispatchFailed = useDispatchNotification({
    message: 'Failed to delete field.',
    variant: 'error',
  });

  /**
   * Deleting from the Outline takes the field out of this document only. One
   * row backs every placement in the workflow, so tombstoning it would pull
   * the field out of documents the user isn't looking at without telling them.
   * Removing the nodes is enough: the Outline lists what the content holds,
   * and the next save drops this document's CannedFieldPlacement. The row goes
   * only when this was its last placement, so nothing is left pointing at a
   * definition no document uses.
   */
  const removeCannedFieldFromDocument = useCallback<
    ReturnType<typeof useRemoveCannedFieldFromDocument>['removeCannedFieldFromDocument']
  >(
    async ({ cannedField, documentId, documentViewer, editor, workflowId }) => {
      try {
        if (editor)
          AlineTransforms.removeCannedFieldsById(editor, { cannedFieldId: cannedField.nodeId });
        if (documentViewer) removeCannedFieldWidgets(documentViewer, cannedField.nodeId);

        const isLastPlacement = await getIsLastPlacement(client, {
          cannedFieldId: cannedField.id,
          documentId,
          workflowId,
        });
        if (!isLastPlacement) return;

        await deleteCannedField({ variables: { id: cannedField.id } });
      } catch {
        dispatchFailed();
      }
    },
    [client, deleteCannedField, dispatchFailed]
  );

  return { removeCannedFieldFromDocument };
}

function removeCannedFieldWidgets(documentViewer: Core.DocumentViewer, nodeId: UUID): void {
  const annotationManager = documentViewer.getAnnotationManager();
  const widgets = annotationManager
    .getAnnotationsList()
    .filter(
      annot =>
        annot instanceof pdfTronCore.Annotations.WidgetAnnotation &&
        annot.getCustomData(PDFCustomDataKey.cannedFieldId) === nodeId
    );
  if (widgets.length) annotationManager.deleteAnnotations(widgets);
}

/**
 * Placements are the only record of which other documents hold this field, and
 * the cached copy goes stale the moment a collaborator places it somewhere
 * else, so read the workflow list from the network before tombstoning. A
 * failed read keeps the row: an unplaced row is invisible everywhere, while a
 * wrong delete strips the field out of every other document.
 */
async function getIsLastPlacement(
  client: ReturnType<typeof useApolloClient>,
  args: { cannedFieldId: UUID; documentId: UUID; workflowId: UUID }
): Promise<boolean> {
  const { cannedFieldId, documentId, workflowId } = args;
  try {
    const { data } = await client.query<
      Apollo.GetWorkflowCannedFieldsQuery,
      Apollo.GetWorkflowCannedFieldsQueryVariables
    >({
      fetchPolicy: 'network-only',
      query: Apollo.GetWorkflowCannedFieldsDocument,
      variables: { workflowId },
    });
    const cannedField = data?.workflow?.cannedFields.find(f => f.id === cannedFieldId);
    if (!cannedField) return false;

    return cannedField.placements.every(placement => placement.documentId === documentId);
  } catch {
    return false;
  }
}

export function useWorkflowCannedFields(workflowId: UUID | null): {
  cannedById: Map<string, Apollo.CannedFieldFullFragment>;
  cannedFields: Apollo.CannedFieldFullFragment[];
  hasError: boolean;
  loading: boolean;
} {
  const { data, error, loading } = Apollo.useGetWorkflowCannedFieldsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });
  /**
   * Apollo stabilizes the array reference across renders when the query result
   * hasn't changed, but `?? []` and `new Map(...)` would still produce fresh
   * references on every render — enough to break consumers that use these in
   * hook dependency lists (`useOnChange` in MobileSigningFieldSheet crashed
   * with "Too many re-renders" on mobile before this memo).
   */
  const { cannedById, cannedFields } = useMemo(() => {
    const cannedFields = data?.workflow?.cannedFields ?? [];
    /**
     * Keyed by the natural key that Slate / PDF nodes reference, not the PK —
     * a template-launched copy keeps the source `nodeId` on the fresh row.
     */
    const cannedById = new Map(cannedFields.map(f => [f.nodeId, f]));
    return { cannedById, cannedFields };
  }, [data?.workflow?.cannedFields]);
  return {
    cannedById,
    cannedFields,
    hasError: Boolean(error),
    loading: loading && !data,
  };
}

export function useCannedFieldsForDoc(documentId: UUID | null): {
  cannedById: Map<string, Apollo.CannedFieldFullFragment>;
  cannedFields: Apollo.CannedFieldFullFragment[];
  hasError: boolean;
  loading: boolean;
  wasSentForSignature: boolean;
  workflowId: UUID | null;
} {
  const { document } = useGetCurrentDoc(documentId ?? undefined);
  const workflowId = document?.workflow?.id ?? null;
  const workflowFields = useWorkflowCannedFields(workflowId);
  const { isSigned, wasSentForSignature } = useDocumentSignatureStatus(documentId ?? '');
  const documentFields = useMemo(() => {
    const cannedFields = workflowFields.cannedFields.filter(field =>
      field.placements.some(placement => placement.documentId === documentId)
    );
    const cannedById = new Map(cannedFields.map(field => [field.nodeId, field]));
    return { cannedById, cannedFields };
  }, [documentId, workflowFields.cannedFields]);

  if (isSigned)
    return {
      ...documentFields,
      hasError: workflowFields.hasError,
      loading: workflowFields.loading,
      wasSentForSignature,
      workflowId,
    };

  return { ...workflowFields, wasSentForSignature, workflowId };
}

export function useScrollCannedFieldIntoView(tabId: UUID | null): {
  scrollCannedFieldIntoView: (cannedFieldId: UUID) => void;
} {
  const editor = useMaybeEditor();
  const documentViewer = useMaybeDocumentViewer();
  const rWHeightPx = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.reactWindow.windowHeight
  );
  const rWOffsetPx = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.reactWindow.scrollOffset
  );

  const scrollCannedFieldIntoView = useCallback(
    (cannedFieldId: UUID): void => {
      if (editor) {
        /**
         * The caret is already inside a placement of this canned field — the
         * focus almost certainly arrived *from* the document (the user clicked
         * or keyed into it). Scrolling would re-select a different placement
         * and yank them away from the one they picked.
         */
        const currentEntry = Editor.above(editor, { match: isCannedFieldElement });
        if (currentEntry?.[0].cannedFieldId === cannedFieldId) return;

        const entry = Queries.getNextCannedFieldElement(editor, { cannedFieldId });
        if (entry) {
          const [, cannedPath] = entry;
          ClientTransforms.scrollToLocation(editor, {
            affinity: 'last',
            cMLocation: Editor.start(editor, cannedPath),
            rWHeightPx,
            rWOffsetPx,
          });
          return;
        }
      }
      if (documentViewer) {
        const widgets = documentViewer
          .getAnnotationManager()
          .getAnnotationsList()
          .filter(annot => annot.getCustomData(PDFCustomDataKey.cannedFieldId) === cannedFieldId);
        if (!widgets.length) return;

        /**
         * A PDF has no caret, so the placement the user clicked is carried on
         * the focused field instead. Same rule as the editor: if they are
         * already standing on a placement, leave the viewer where it is.
         */
        const focusedField = tabId ? getVariablePanelFocusedField(tabId) : null;
        const focusedPlacementNodeId =
          focusedField?.__typename === 'CannedField' ? focusedField.placementNodeId : null;
        // `getCustomData` returns '' for an absent key, so only compare when we
        // actually have a placement to compare against.
        const isOnPlacement =
          Boolean(focusedPlacementNodeId) &&
          widgets.some(
            widget => widget.getCustomData(PDFCustomDataKey.nodeId) === focusedPlacementNodeId
          );
        if (isOnPlacement) return;

        const [target] = widgets;
        if (target)
          documentViewer.displayPageLocation(target.getPageNumber(), target.X, target.Y, true);
      }
    },
    [documentViewer, editor, rWHeightPx, rWOffsetPx, tabId]
  );

  return { scrollCannedFieldIntoView };
}

export function useCannedFieldIdsInCurrentDoc(tabId: UUID | null): Set<UUID> {
  const documentViewer = useMaybeDocumentViewer();
  const annotationsLoaded = useAreAnnotationsLoaded();
  const { cannedFieldIdsInChildren } = useGetFieldsInJSON();
  const annotationsChangedCount = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.viewer.annotationsChangedCount
  );

  return useMemo(() => {
    // Read annotationsChangedCount so the memo recomputes when PDF widgets are
    // added/removed. The value itself is unused; the reference is the signal.
    void annotationsChangedCount;
    const ids = new Set(cannedFieldIdsInChildren);
    if (documentViewer && annotationsLoaded) {
      for (const annot of documentViewer.getAnnotationManager().getAnnotationsList()) {
        const cannedFieldId = annot.getCustomData(PDFCustomDataKey.cannedFieldId);
        if (cannedFieldId) ids.add(cannedFieldId);
      }
    }
    return ids;
  }, [annotationsChangedCount, annotationsLoaded, cannedFieldIdsInChildren, documentViewer]);
}

export function useSyncCannedFieldValuesToSlate(
  editor: AlineClientEditor | null,
  workflowId: UUID | null,
  showDefaultPreview: boolean,
  editorLoading: boolean
): void {
  const { cannedFields } = useWorkflowCannedFields(workflowId);

  useEffect(() => {
    if (!editor || editorLoading) return;
    for (const cannedField of cannedFields) {
      const fallback = CANNED_FIELD_METADATA[cannedField.variant].label;
      const text = showDefaultPreview
        ? (cannedField.value ?? cannedField.defaultValue ?? fallback)
        : (cannedField.value ?? fallback);
      AlineTransforms.setCannedFieldValueText(editor, {
        cannedFieldId: cannedField.nodeId,
        text,
      });
    }
  }, [cannedFields, editor, editorLoading, showDefaultPreview]);
}

export function useSyncCannedFieldValuesToPdf(
  documentViewer: Core.DocumentViewer | null,
  workflowId: UUID | null,
  tabId: UUID | null,
  showDefaultPreview: boolean
): void {
  const { cannedById, cannedFields } = useWorkflowCannedFields(workflowId);
  const { setPDFCannedFieldValue } = usePDFFieldAPI(documentViewer, tabId);
  const annotationsLoaded = useAreAnnotationsLoaded();
  const annotationsChangedCount = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.viewer.annotationsChangedCount
  );

  useEffect(() => {
    /**
     * Read annotationsChangedCount so a newly placed widget is filled in
     * without a reload. Placing a second copy of an existing canned field
     * changes none of the other deps — the DB row already exists, so
     * `cannedFields` is untouched — and the new widget would stay blank.
     * The value itself is unused; the reference is the signal.
     */
    void annotationsChangedCount;
    if (!documentViewer || !annotationsLoaded) return;
    const annotationManager = documentViewer.getAnnotationManager();
    for (const cannedField of cannedFields) {
      const text = showDefaultPreview
        ? (cannedField.value ?? cannedField.defaultValue ?? '')
        : (cannedField.value ?? '');
      setPDFCannedFieldValue(cannedField.nodeId, text);
    }
    for (const annot of annotationManager.getAnnotationsList()) {
      if (!(annot instanceof pdfTronCore.Annotations.WidgetAnnotation)) continue;
      const cannedFieldId = annot.getCustomData(PDFCustomDataKey.cannedFieldId);
      if (!cannedFieldId) continue;
      const cannedField = cannedById.get(cannedFieldId);
      if (!cannedField || !annot.innerElement) continue;
      annot.innerElement.title = CANNED_FIELD_METADATA[cannedField.variant].buildTooltip(
        cannedField.value ?? cannedField.defaultValue ?? null
      );
    }
  }, [
    annotationsChangedCount,
    annotationsLoaded,
    cannedById,
    cannedFields,
    documentViewer,
    setPDFCannedFieldValue,
    showDefaultPreview,
  ]);
}

/**
 * Verify each candidate against the server before treating it as orphan. The
 * cached workflow canned list is a hint, not the source of truth — it can be
 * empty during error, stale during a Yjs sync that beat the Apollo refetch,
 * or missing entries created moments ago by another collaborator. A single
 * `network-only` refetch of the workflow list keeps live placements safe:
 * only nodeIds absent from the fresh server response are treated as orphan.
 * Candidates are the `cannedFieldId` values Slate/PDF nodes carry (nodeIds),
 * not row PKs.
 */
async function filterCandidatesConfirmedGone(
  client: ReturnType<typeof useApolloClient>,
  workflowId: UUID,
  candidates: Iterable<UUID>
): Promise<Set<UUID>> {
  const candidatesArr = [...candidates];
  if (!candidatesArr.length) return new Set();
  try {
    const { data } = await client.query<
      Apollo.GetWorkflowCannedFieldsQuery,
      Apollo.GetWorkflowCannedFieldsQueryVariables
    >({
      fetchPolicy: 'network-only',
      query: Apollo.GetWorkflowCannedFieldsDocument,
      variables: { workflowId },
    });
    if (!data?.workflow) return new Set();
    const liveNodeIds = new Set(data.workflow.cannedFields.map(f => f.nodeId));
    return new Set(candidatesArr.filter(nodeId => !liveNodeIds.has(nodeId)));
  } catch {
    // Server unreachable / error — skip; safer to leave widget than to wipe.
    return new Set();
  }
}

/**
 * Purge slate CANNED voids whose backing workflow canned no longer exists
 * (e.g. an author deleted the canned from another doc's Outline while this
 * doc kept the widget). Runs whenever the workflow canned list resolves and
 * confirms each candidate against the server before removing, so an errored
 * or stale Apollo cache cannot destroy live placements. Removals flow
 * through yjs and the doc-save pipeline so `syncCannedFieldPlacementsForDoc`
 * drops the stale CannedFieldPlacement row on the next save.
 */
export function useCleanupOrphanCannedFieldsInSlate(
  editor: AlineClientEditor | null,
  workflowId: UUID | null,
  editorLoading: boolean
): void {
  const { cannedById, loading: cannedLoading } = useWorkflowCannedFields(workflowId);
  const client = useApolloClient();
  useEffect(() => {
    if (!editor || editorLoading || cannedLoading || !workflowId) return;
    const candidates = new Set<UUID>();
    for (const [{ cannedFieldId }] of Queries.cannedFieldEntries(editor)) {
      if (!cannedById.has(cannedFieldId)) candidates.add(cannedFieldId);
    }
    if (!candidates.size) return;
    let cancelled = false;
    void filterCandidatesConfirmedGone(client, workflowId, candidates).then(orphans => {
      if (cancelled) return;
      for (const cannedFieldId of orphans) {
        AlineTransforms.removeCannedFieldsById(editor, { cannedFieldId });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [cannedById, cannedLoading, client, editor, editorLoading, workflowId]);
}

/**
 * PDF-side counterpart of useCleanupOrphanCannedFieldsInSlate. Same
 * server-verification safety net — a candidate is removed only after the
 * server confirms its CannedField row is gone. The annotation-changed
 * subscription then flushes the deletion to XFDF on the server.
 */
export function useCleanupOrphanCannedFieldsInPdf(
  documentViewer: Core.DocumentViewer | null,
  workflowId: UUID | null
): void {
  const { cannedById, loading: cannedLoading } = useWorkflowCannedFields(workflowId);
  const client = useApolloClient();
  const annotationsLoaded = useAreAnnotationsLoaded();
  useEffect(() => {
    if (!documentViewer || !annotationsLoaded || cannedLoading || !workflowId) return;
    const annotationManager = documentViewer.getAnnotationManager();
    // One canned field can be placed many times, so each id maps to every
    // widget carrying it — keying a single widget per id would leave all but
    // one orphan widget on the page.
    const candidateWidgetsById = new Map<UUID, Core.Annotations.WidgetAnnotation[]>();
    for (const annot of annotationManager.getAnnotationsList()) {
      if (!(annot instanceof pdfTronCore.Annotations.WidgetAnnotation)) continue;
      const cannedFieldId = annot.getCustomData(PDFCustomDataKey.cannedFieldId);
      if (!cannedFieldId || cannedById.has(cannedFieldId)) continue;
      candidateWidgetsById.set(cannedFieldId, [
        ...(candidateWidgetsById.get(cannedFieldId) ?? []),
        annot,
      ]);
    }
    if (!candidateWidgetsById.size) return;
    let cancelled = false;
    void filterCandidatesConfirmedGone(client, workflowId, candidateWidgetsById.keys()).then(
      orphanIds => {
        if (cancelled || !orphanIds.size) return;
        const orphans = [...orphanIds].flatMap(id => candidateWidgetsById.get(id) ?? []);
        if (orphans.length) annotationManager.deleteAnnotations(orphans);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [annotationsLoaded, cannedById, cannedLoading, client, documentViewer, workflowId]);
}

export function useRefetchCannedFieldsOnDocumentUpdated(documentId: UUID | null): void {
  Apollo.useOnDocumentUpdatedSubscription({
    onData: ({ client }) => {
      void client.refetchQueries({
        include: [Apollo.GetSignedDocumentDocument, Apollo.GetWorkflowCannedFieldsDocument],
        /**
         * `skip` stops a query from fetching but not from being refetched by an
         * `include` list — Apollo filters skipped (standby) queries out only for
         * `include: 'active'`. A document with no workflow still registers this
         * query with a placeholder `workflowId: ''`, which the server rejects as
         * a UUID. Refetch only the instances holding a real id. Tabs stay
         * mounted, so this has to be decided per query rather than from the
         * current document's workflow.
         */
        onQueryUpdated: observableQuery => {
          const variables = observableQuery.options.variables;
          return Boolean(variables?.workflowId) || variables?.documentId === documentId;
        },
      });
    },
    shouldResubscribe: true,
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });
}
